import React, { useEffect, useId, useRef, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { CloudUpload, Delete, InsertDriveFile } from "@mui/icons-material";
import { singleFileUpload, removeFile } from "../../services/fileUploadService";
import { toastAlert } from "../../helpers/toastAlert";
import {
  ATTACHMENT_ACCEPT,
  isImageFile,
  isPdfFile,
  validateAttachmentFile,
} from "../../utils/attachmentUtils";
import { getDocumentFileBlob } from "../../services/Documentservice";
import { fileKindIcon } from "./FilePreview";

const FileUploader = ({
  onUploadSuccess,
  onDeleteSuccess,
  thisEvt,
  customerId,
  resetUploader,
  props,
  defaultFileId,
  label = "Upload file",
}) => {
  const inputId = useId();
  const localPreviewRef = useRef(null);
  const remotePreviewRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewKind, setPreviewKind] = useState("none"); // image | pdf | other | none
  const [previewFileId, setPreviewFileId] = useState(null);
  const [fileMeta, setFileMeta] = useState(null);
  const [uploading, setUploading] = useState(false);

  const authProps = props || thisEvt?.props;
  const token = authProps?.authData?.accessToken;

  const revokeLocal = () => {
    if (localPreviewRef.current) {
      URL.revokeObjectURL(localPreviewRef.current);
      localPreviewRef.current = null;
    }
  };
  const revokeRemote = () => {
    if (remotePreviewRef.current) {
      URL.revokeObjectURL(remotePreviewRef.current);
      remotePreviewRef.current = null;
    }
  };

  const clearPreview = () => {
    revokeLocal();
    revokeRemote();
    setPreviewUrl(null);
    setPreviewKind("none");
    setPreviewFileId(null);
    setFileMeta(null);
  };

  useEffect(() => {
    clearPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetUploader]);

  useEffect(() => {
    return () => {
      revokeLocal();
      revokeRemote();
    };
  }, []);

  const openRemoteFile = async (fileId) => {
    if (!fileId || !token) return;
    try {
      revokeRemote();
      const result = await getDocumentFileBlob(fileId, token);
      remotePreviewRef.current = result.url;
      const meta = {
        _id: fileId,
        fileName: fileMeta?.fileName || "attachment",
        fileType: result.mime,
      };
      setFileMeta(meta);
      if (isImageFile(meta)) {
        setPreviewKind("image");
        setPreviewUrl(result.url);
      } else if (isPdfFile(meta)) {
        setPreviewKind("pdf");
        setPreviewUrl(result.url);
      } else {
        setPreviewKind("other");
        setPreviewUrl(null);
      }
      setPreviewFileId(fileId);
    } catch (err) {
      console.error("Open file failed", err);
      toastAlert(err?.message || "Could not open file", "error");
    }
  };

  useEffect(() => {
    if (defaultFileId) {
      openRemoteFile(defaultFileId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFileId, token]);

  const handleFileChange = async (event) => {
    if (!customerId) {
      toastAlert("Please select Customer First", "error");
      event.target.value = "";
      return;
    }

    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const validationError = validateAttachmentFile(file);
    if (validationError) {
      toastAlert(validationError, "error");
      return;
    }

    revokeLocal();
    const localUrl = URL.createObjectURL(file);
    localPreviewRef.current = localUrl;
    setFileMeta({ fileName: file.name, fileType: file.type });

    if (isImageFile({ fileName: file.name, fileType: file.type })) {
      setPreviewKind("image");
      setPreviewUrl(localUrl);
    } else if (isPdfFile({ fileName: file.name, fileType: file.type })) {
      setPreviewKind("pdf");
      setPreviewUrl(localUrl);
    } else {
      setPreviewKind("other");
      setPreviewUrl(null);
    }

    try {
      setUploading(true);
      const response = await singleFileUpload(
        { file, customerId },
        authProps,
      );

      const uploadedFile =
        response?.data?.data ||
        response?.data ||
        response?.response?.data?.data;
      const uploadedId = uploadedFile?._id;

      if (
        uploadedId &&
        (response?.status === 200 ||
          response?.status === 201 ||
          response?.data?.code === 200 ||
          response?.data?.code === 201)
      ) {
        setPreviewFileId(uploadedId);
        onUploadSuccess?.(uploadedId, uploadedFile);
        toastAlert("File uploaded", "success");
      } else if (uploadedId) {
        setPreviewFileId(uploadedId);
        onUploadSuccess?.(uploadedId, uploadedFile);
        toastAlert("File uploaded", "success");
      } else {
        toastAlert(
          response?.response?.data?.message ||
            response?.data?.message ||
            "Upload failed",
          "error",
        );
        clearPreview();
      }
    } catch (error) {
      console.error("Upload error", error);
      toastAlert("Upload failed", "error");
      clearPreview();
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!previewFileId) {
      clearPreview();
      return;
    }

    try {
      await removeFile({ documentId: previewFileId }, authProps);
      onDeleteSuccess?.(previewFileId);
      clearPreview();
      toastAlert("File removed", "success");
    } catch (error) {
      console.error("Delete error", error);
      toastAlert("Failed to remove file", "error");
    }
  };

  return (
    <Box>
      <input
        hidden
        type="file"
        id={inputId}
        accept={ATTACHMENT_ACCEPT}
        onChange={handleFileChange}
        disabled={uploading}
      />

      <label htmlFor={inputId}>
        <Button
          variant="contained"
          component="span"
          startIcon={<CloudUpload />}
          disabled={uploading}
          sx={{ textTransform: "none", backgroundColor: "#1a3366" }}
        >
          {uploading ? "Uploading…" : label}
        </Button>
      </label>

      {(previewUrl || previewKind === "other" || previewFileId) && (
        <Box mt={2} display="flex" alignItems="center" gap={2}>
          <Box position="relative" width={previewKind === "image" ? 120 : 160}>
            {previewKind === "image" && previewUrl && (
              <img
                src={previewUrl}
                alt="Preview"
                width={120}
                height={120}
                style={{ borderRadius: 6, objectFit: "cover" }}
              />
            )}
            {previewKind === "pdf" && previewUrl && (
              <Box
                sx={{
                  width: 160,
                  height: 120,
                  border: "1px solid #e2e8f0",
                  borderRadius: 1,
                  overflow: "hidden",
                  bgcolor: "#f8fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 0.5,
                  p: 1,
                }}
              >
                {fileKindIcon({
                  fileName: fileMeta?.fileName || "file.pdf",
                  fileType: "application/pdf",
                })}
                <Typography variant="caption" noWrap sx={{ maxWidth: 140 }}>
                  {fileMeta?.fileName || "PDF"}
                </Typography>
              </Box>
            )}
            {previewKind === "other" && (
              <Box
                sx={{
                  width: 160,
                  height: 80,
                  border: "1px solid #e2e8f0",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.5,
                  bgcolor: "#f8fafc",
                }}
              >
                <InsertDriveFile color="primary" />
                <Typography variant="caption" noWrap sx={{ maxWidth: 110 }}>
                  {fileMeta?.fileName || "Attachment"}
                </Typography>
              </Box>
            )}

            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                background: "#fff",
              }}
            >
              <Delete color="error" />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FileUploader;
